import React, { useState } from 'react';
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { Gift } from 'react-feather';
import { OrderPageData, VoucherSoldTypes } from '../../api';
import { StepId } from '../intentConfig/types';
import { OrderPageIntent } from '../OrderPageIntent';
import { OrderPageButton } from '../themedComponents/OrderPageButton';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { OrderPageDivider } from '../themedComponents/OrderPageDivider';
import { PriceSummary } from './PriceSummary';
import { useOrderPage } from '../orderPageContext/useOrderPage';
import { useIntentConfig } from '../intentConfig/useIntentConfig';
import { safeStartSessionRecording } from '../../utils/safePosthog';
import ConfirmationDialog from './ConfimationDialog';

type OrderPageActionsProps = {
  loading: boolean;
  shouldDisableNext: boolean;
  onNext: (intent?: OrderPageIntent) => Promise<void>;
  onBack: () => void;
  onResetDialogOpen: () => void;
  nextButtonText: () => string;
};

const OrderPageActions = ({
  loading,
  shouldDisableNext,
  onNext,
  onBack,
  onResetDialogOpen,
  nextButtonText,
}: OrderPageActionsProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { orderPageState } = useOrderPage();
  const { config, currentStepId, currentStepIndex } = useIntentConfig();
  const { orderPageIntent, specificOrderPage } = orderPageState;
  const specificEvent = !!orderPageState?.specificEvent;
  const shouldShowStartOverButton =
    currentStepId === StepId.PAYMENT && orderPageIntent !== OrderPageIntent.REDEEM_GIFT_CARD;
  const isGiftCardTypeStep = currentStepId === StepId.GIFT_CARD_TYPE;
  const [isGiftCardDateWarningOpen, setIsGiftCardDateWarningOpen] = useState(false);
  const soldTypes =
    (orderPageState.orderPage as OrderPageData).effective_vouchers_config?.sold_types ?? VoucherSoldTypes.EXPERIENCE;
  const showsPrice =
    currentStepIndex === 0 &&
    !isGiftCardTypeStep &&
    orderPageIntent !== OrderPageIntent.REDEEM_GIFT_CARD &&
    orderPageIntent !== OrderPageIntent.BUY_PASS;

  const panelSx = showsPrice
    ? {
        boxSizing: 'border-box',
        // .order-page-box drops its 24px padding under $xs, so without an inset of our own the
        // frame sits flush on the card border. It comes out of the width because this Grid is a
        // flex item pinned to 100%, and a margin alone would overflow.
        width: isSmallScreen ? 'calc(100% - 16px)' : '100%',
        mx: isSmallScreen ? 1 : 0,
        backgroundColor: theme.customTheme.palette.background,
        border: `1px solid ${theme.customTheme.palette.border}`,
        borderRadius: '12px',
      }
    : undefined;
  const hasSecondaryButton = shouldShowStartOverButton || currentStepIndex !== 0;

  const startGiftCardFlow = (intent: OrderPageIntent) => {
    safeStartSessionRecording(posthog);
    posthog.capture('Button clicked', {
      name: 'Start voucher',
      orderPageId: orderPageState.orderPage.id,
    });
    void onNext(intent);
  };

  const handleGiftCardClick = () => {
    if (soldTypes === VoucherSoldTypes.MONETARY) {
      startGiftCardFlow(OrderPageIntent.MONETARY_GIFT_CARD);
      return;
    }
    if (orderPageState.dateAndTimeDetails.date) {
      setIsGiftCardDateWarningOpen(true);
      return;
    }
    startGiftCardFlow(OrderPageIntent.GIFT_CARD);
  };

  return (
    <>
      {currentStepId !== StepId.PAYMENT && currentStepId !== StepId.REDEMPTION_SUMMARY && (
        <Grid
          container
          px={showsPrice ? 2 : 0.5}
          py={showsPrice ? 2 : 0}
          mx={currentStepIndex !== 0 && !isSmallScreen ? 4 : 0}
          direction="column"
          wrap="nowrap"
          sx={panelSx}>
          {!isGiftCardTypeStep && (
            <Grid item xs={12} mb={1}>
              <PriceSummary muted={shouldDisableNext} />
            </Grid>
          )}

          <Grid
            item
            container
            justifyContent={hasSecondaryButton ? 'space-between' : 'center'}
            alignItems={isSmallScreen || !hasSecondaryButton ? 'center' : undefined}
            direction={isSmallScreen && currentStepIndex === 0 ? 'column' : 'row'}>
            {hasSecondaryButton && (
              <Grid item>
                {shouldShowStartOverButton ? (
                  <OrderPageButton variant="outlined" color="inherit" onClick={onResetDialogOpen}>
                    <OrderPageTypography color="button.outlined">{t('reset')}</OrderPageTypography>
                  </OrderPageButton>
                ) : (
                  <OrderPageButton variant="outlined" color="inherit" onClick={onBack}>
                    <OrderPageTypography color="button.outlined">{t('back')}</OrderPageTypography>
                  </OrderPageButton>
                )}
              </Grid>
            )}

            <Grid
              item
              xs={isSmallScreen || !hasSecondaryButton ? undefined : 6}
              flexDirection="column"
              display={
                (specificOrderPage?.onlyVouchers && !specificOrderPage?.standaloneGiftCard && currentStepIndex === 0) ||
                isGiftCardTypeStep
                  ? 'none'
                  : 'flex'
              }
              alignItems={hasSecondaryButton && !isSmallScreen ? 'flex-end' : 'center'}>
              <OrderPageButton
                disabled={orderPageState.isLoadingDates || shouldDisableNext || specificOrderPage?.troubleshoot}
                loadingButton
                sx={{
                  width: '150px',
                }}
                loading={loading}
                variant="contained"
                color="primary"
                onClick={() => {
                  safeStartSessionRecording(posthog);
                  posthog.capture('Button clicked', {
                    name: 'Start order',
                    orderPageId: orderPageState.orderPage.id,
                    accountId: orderPageState.account.id,
                  });
                  void onNext();
                }}
                className="next-button">
                <OrderPageTypography variant="h6" color="button.contained" bold>
                  {nextButtonText()}
                </OrderPageTypography>
              </OrderPageButton>
            </Grid>
          </Grid>
        </Grid>
      )}

      {currentStepIndex === 0 &&
        config?.canSwitchToGiftCard &&
        !specificEvent &&
        soldTypes !== VoucherSoldTypes.NONE && (
          <Grid container flexDirection="column" gap={2} my={3}>
            {!specificOrderPage?.onlyVouchers && (
              <OrderPageDivider>
                <OrderPageTypography>{t('order_page.divider')}</OrderPageTypography>
              </OrderPageDivider>
            )}
            <Grid item display="flex" justifyContent="space-around" flexDirection="column" alignItems="center" gap={2}>
              {!specificOrderPage?.onlyVouchers && soldTypes !== VoucherSoldTypes.MONETARY && (
                <OrderPageTypography>{t('vouchersremark')}</OrderPageTypography>
              )}
              <OrderPageButton
                sx={{
                  width: '150px',
                }}
                startIcon={<Gift size={16} color={theme.customTheme.palette.text.button.outlined} />}
                variant="outlined"
                onClick={handleGiftCardClick}
                className="next-button"
                disabled={specificOrderPage?.troubleshoot || orderPageState.isLoadingDates}>
                <OrderPageTypography variant="h6" color="button.outlined">
                  {soldTypes === VoucherSoldTypes.MONETARY
                    ? t('purchaseGiftCard')
                    : soldTypes === VoucherSoldTypes.BOTH
                    ? t('purchaseGiftVoucher')
                    : specificOrderPage?.onlyVouchers
                    ? t('purchaseVoucher')
                    : t('purchasegift')}
                </OrderPageTypography>
              </OrderPageButton>
            </Grid>
          </Grid>
        )}

      <ConfirmationDialog
        isOpen={isGiftCardDateWarningOpen}
        title={t('giftCardDateWarning.title')}
        content={t('giftCardDateWarning.content')}
        ctaText={t('giftCardDateWarning.cta')}
        confirmButtonColor="primary"
        loadingButton={false}
        onClose={(actionConfirmed: boolean) => {
          setIsGiftCardDateWarningOpen(false);
          if (actionConfirmed) {
            startGiftCardFlow(OrderPageIntent.GIFT_CARD);
          }
        }}
      />
    </>
  );
};

export default OrderPageActions;
